// AdminLayout.tsx - Admin Dashboard Layout
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Sidebar from '../UI/Sidebar';
import Dashboard from '../Dashboard/Dashboard';
import VendorList from '../Vendors/VendorList';
import MenuManagement from '../Vendors/MenuManagement';
import UsersManagement from '../Users/UsersManagement';
import EventsPromotions from '../Events/EventsPromotions';
import VenueSettings from '../Settings/VenueSettings';
import Orders from '../Orders/Orders';
import Analytics from '../Analytics/Analytics';
import Modal from '../UI/Modal';
import modalStyles from '../UI/Modal.module.css';
import VendorForm from '../Forms/VendorForm';
import VendorDetailsForm, { VendorDetailsFormData } from '../Forms/VendorDetailsForm';
import StadiumMap from '../Forms/StadiumMap';
import MenuItemForm from '../Forms/MenuItemForm';
import Profile from '../Profile/Profile';
import NoAccess from '../UI/NoAccess';
import useVendors from '../../hooks/useVendors';
import usePermissions from '../../hooks/usePermissions';
import eventService from '../../services/eventService';
import toastService from '../../services/toastService';
import { Vendor, FoodMenuItem } from '../../utils/constants';
import styles from './AdminLayout.module.css';

type Permission = 'dashboard' | 'vendors' | 'users' | 'events' | 'orders' | 'analytics';

interface Promotion {
  status: string;
  analytics?: {
    clicks?: number;
    ctr?: number;
  };
}

interface CurrentUser {
  id: string;
  name: string;
  role: string;
  phoneNumber: string;
  email: string;
  isAdmin: boolean;
  permissions: Permission[];
  vendorId?: string | null;
}

const AdminLayout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Derive active menu from current URL path
  const getMenuFromPath = (pathname: string): string => {
    const validMenus = ['dashboard', 'vendors', 'users', 'events', 'orders', 'analytics', 'menu', 'profile', 'venue-settings'];
    const parts = pathname.replace(/^\/admin\/?/, '').split('/');
    const path = parts[0] || 'dashboard';
    return validMenus.includes(path) ? path : 'dashboard';
  };

  const [activeMenu, setActiveMenu] = useState(() => getMenuFromPath(location.pathname));
  const [currentUser, setCurrentUser] = useState<CurrentUser | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  
  // Vendor management state
  const {
    vendors,
    loading: vendorsLoading,
    addVendor,
    updateVendor,
    addMenuItem,
    updateMenuItem,
    deleteMenuItem,
    deleteVendor,
  } = useVendors();
  const { can, canManageVenueSettings } = usePermissions();

  const canViewMenu = (menuId: string): boolean => {
    if (menuId === 'venue-settings') return canManageVenueSettings;
    if (menuId === 'dashboard') return can('dashboard');
    if (menuId === 'vendors') return can('vendors');
    if (menuId === 'users') return can('users');
    if (menuId === 'events') return can('events');
    if (menuId === 'orders') return can('orders');
    if (menuId === 'analytics') return can('analytics');
    if (menuId === 'profile') return true;
    return false;
  };
  
  // Modal and form state
  const [selectedVendor, setSelectedVendor] = useState<Vendor | null>(null);
  const [showVendorModal, setShowVendorModal] = useState(false);
  const [showSectionModal, setShowSectionModal] = useState(false);
  const [showVendorDetailsModal, setShowVendorDetailsModal] = useState(false);
  const [vendorBeingEdited, setVendorBeingEdited] = useState<Vendor | null>(null);
  const [vendorEditingSections, setVendorEditingSections] = useState<Vendor | null>(null);
  const [vendorDetailsValid, setVendorDetailsValid] = useState(false);
  const [showMenuItemModal, setShowMenuItemModal] = useState(false);
  const [editingMenuItem, setEditingMenuItem] = useState<FoodMenuItem | null>(null);
  const [menuFormValid, setMenuFormValid] = useState(false);
  const [selectedSections, setSelectedSections] = useState<Set<string>>(new Set<string>());

  // Events & Promotions state
  const [promotions, setPromotions] = useState<Promotion[]>([]);
  const [promotionStats, setPromotionStats] = useState({
    totalPromotions: 0,
    activePromotions: 0,
    totalClicks: 0,
    averageCTR: 0
  });

  // Global notification shim (ToastContainer in App.tsx)
  const showNotification = (message: string, type = 'info') => {
    switch (type) {
      case 'success':
        toastService.success(message);
        break;
      case 'error':
        toastService.error(message);
        break;
      case 'warning':
        toastService.warning(message);
        break;
      default:
        toastService.info(message);
    }
  };

  // Keep selected vendor in sync with vendor list updates
  useEffect(() => {
    if (!selectedVendor) return;
    const fresh = vendors.find((v) => v.id === selectedVendor.id);
    if (fresh) setSelectedVendor(fresh);
    else setSelectedVendor(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only re-sync when vendors list changes
  }, [vendors, selectedVendor?.id]);

  // Sync active menu with URL changes (back/forward)
  useEffect(() => {
    const menu = getMenuFromPath(location.pathname);
    setActiveMenu(menu);
  }, [location.pathname]);

  // Navigate to menu section via React Router
  const handleMenuChange = (menu: string) => {
    const path = menu === 'dashboard' ? '/admin' : `/admin/${menu}`;
    navigate(path);
    setSelectedVendor(null);
  };

  // Load current user from localStorage
  useEffect(() => {
    const storedUser = localStorage.getItem('oni_current_user');
    if (storedUser) {
      try {
        setCurrentUser(JSON.parse(storedUser));
      } catch (error) {
        console.error('Failed to parse user data:', error);
        localStorage.removeItem('oni_current_user');
      }
    }
  }, []);

  // Load promotions data for cross-component sharing
  useEffect(() => {
    if (!currentUser) return; // Skip if not logged in

    const loadPromotions = async () => {
      try {
        const promotionsList = await eventService.getAll({ type: 'promotion' });
        setPromotions(promotionsList);
        
        // Calculate stats for dashboard and analytics
        const stats = {
          totalPromotions: promotionsList.length,
          activePromotions: promotionsList.filter((p: Promotion) => p.status === 'active').length,
          totalClicks: promotionsList.reduce((sum: number, p: Promotion) => sum + (p.analytics?.clicks || 0), 0),
          averageCTR: promotionsList.length > 0 
            ? promotionsList.reduce((sum: number, p: Promotion) => sum + (p.analytics?.ctr || 0), 0) / promotionsList.length 
            : 0
        };
        setPromotionStats(stats);
      } catch {
        // Silently handle failure — ad blockers commonly block /api/events?type=promotion.
        setPromotions([]);
        setPromotionStats({
          totalPromotions: 0,
          activePromotions: 0,
          totalClicks: 0,
          averageCTR: 0
        });
      }
    };

    loadPromotions();

    // Listen for promotion updates from EventsPromotions component
    const handlePromotionUpdate = () => {
      loadPromotions();
    };

    window.addEventListener('promotionsUpdated', handlePromotionUpdate);
    return () => window.removeEventListener('promotionsUpdated', handlePromotionUpdate);
  }, [currentUser]);

  // Navigation handlers
  const handleMenuClick = (menuItem: string) => {
    handleMenuChange(menuItem);
  };

  const handleVendorManage = (vendor: Vendor) => {
    setSelectedVendor(vendor);
    // Use navigate directly — handleMenuChange calls setSelectedVendor(null) which would clear the vendor
    navigate('/admin/vendors');
  };

  const handleBackToVendors = () => {
    setSelectedVendor(null);
  };

  // Vendor form handlers
  const handleCreateVendor = async (vendorData: { name: string; sections: string[] }) => {
    try {
      await addVendor(vendorData);
      setShowVendorModal(false);
      
      // Add success notification
      showNotification('Vendor created successfully', 'success');
    } catch (error) {
      console.error('Error creating vendor:', error);
      showNotification('Failed to create vendor', 'error');
    }
  };

  const handleEditSections = (vendor: Vendor) => {
    setVendorEditingSections(vendor);
    setSelectedSections(new Set(vendor.sections));
    setShowSectionModal(true);
  };

  const closeSectionsModal = () => {
    setShowSectionModal(false);
    setVendorEditingSections(null);
    setSelectedSections(new Set());
  };

  const handleEditDetails = (vendor: Vendor) => {
    setVendorBeingEdited(vendor);
    setVendorDetailsValid(false);
    setShowVendorDetailsModal(true);
  };

  const handleSaveVendorDetails = async (formData: VendorDetailsFormData) => {
    if (!vendorBeingEdited) return;

    try {
      await updateVendor(vendorBeingEdited.id, formData);
      showNotification('Vendor updated successfully', 'success');
      setShowVendorDetailsModal(false);
      setVendorBeingEdited(null);
      setVendorDetailsValid(false);
    } catch (error) {
      console.error('Error updating vendor details:', error);
      showNotification('Failed to update vendor', 'error');
    }
  };

  const handleUpdateSections = async () => {
    if (!vendorEditingSections) return;

    try {
      await updateVendor(vendorEditingSections.id, {
        sections: Array.from(selectedSections) as string[],
      });
      closeSectionsModal();
      showNotification('Sections updated successfully', 'success');
    } catch (error) {
      console.error('Error updating vendor sections:', error);
      showNotification('Failed to update sections', 'error');
    }
  };

  // Menu item handlers
  const handleAddMenuItem = () => {
    setEditingMenuItem(null);
    setShowMenuItemModal(true);
  };

  const handleEditMenuItem = (item: FoodMenuItem) => {
    const freshItem =
      selectedVendor?.menuItems.find((menuItem) => menuItem.id === item.id) ?? item;
    setEditingMenuItem(freshItem);
    setShowMenuItemModal(true);
  };

  const handleSaveMenuItem = async (menuItemData: Omit<FoodMenuItem, 'id'>) => {
    if (!selectedVendor) return;

    try {
      let updatedVendor;
      if (editingMenuItem) {
        updatedVendor = await updateMenuItem(selectedVendor.id, editingMenuItem.id, menuItemData);
        showNotification('Menu item updated successfully', 'success');
      } else {
        updatedVendor = await addMenuItem(selectedVendor.id, menuItemData);
        showNotification('Menu item added successfully', 'success');
      }

      // Update selectedVendor with the data returned from API
      setSelectedVendor(updatedVendor);

      setShowMenuItemModal(false);
      setEditingMenuItem(null);
    } catch (error) {
      console.error('Error saving menu item:', error);
      showNotification('Failed to save menu item', 'error');
    }
  };

  const handleDeleteMenuItem = async (itemId: string) => {
    if (!selectedVendor) return;
    
    try {
      const updatedVendor = await deleteMenuItem(selectedVendor.id, itemId);
      setSelectedVendor(updatedVendor);
      showNotification('Menu item deleted successfully', 'warning');
    } catch (error) {
      console.error('Error deleting menu item:', error);
      showNotification('Failed to delete menu item', 'error');
    }
  };

  // Render main content based on current view
  const dashboardPromotionStats = {
    totalCampaigns: promotionStats.totalPromotions,
    activeCampaigns: promotionStats.activePromotions,
    totalClicks: promotionStats.totalClicks,
    averageCTR: promotionStats.averageCTR.toFixed(2)
  };

  // Enhanced analytics data with promotions
  const enhancedAnalyticsData = {
    vendors,
    promotions,
    promotionStats,
    crossComponentData: {
      vendorCount: vendors.length,
      totalMenuItems: vendors.reduce((sum: number, v: Vendor) => sum + (v.menuItems?.length || 0), 0),
      promotionEngagement: promotionStats.totalClicks
    }
  };

  // Shared data for EventsPromotions component
  const sharedDataForEvents = {
    vendors: vendors.length,
    sections: vendors.reduce((sections: string[], vendor: Vendor) => {
      return [...sections, ...vendor.sections];
    }, [])
  };

  const handleUpdateVendorWait = async (vendor: Vendor, waitMinutes: number | null) => {
    try {
      await updateVendor(vendor.id, { currentWaitMinutes: waitMinutes } as any);
      showNotification('Vendor wait time updated', 'success');
    } catch {
      showNotification('Failed to update wait time', 'error');
    }
  };

  const handleToggleVendorStatus = async (vendor: Vendor, status: 'active' | 'inactive') => {
    try {
      await updateVendor(vendor.id, { status } as any);
      showNotification(`Vendor ${status === 'active' ? 'activated' : 'deactivated'}`, 'success');
    } catch {
      showNotification('Failed to update vendor status', 'error');
    }
  };

  const handleDeleteVendor = async (vendor: Vendor) => {
    try {
      await deleteVendor(vendor.id);
      if (selectedVendor?.id === vendor.id) {
        setSelectedVendor(null);
      }
      showNotification('Vendor deleted', 'success');
    } catch (error) {
      console.error('Error deleting vendor:', error);
      showNotification('Failed to delete vendor', 'error');
    }
  };

  // Render main content based on current view
  const renderMainContent = () => {
    if (selectedVendor) {
      if (!can('vendors')) {
        return <NoAccess />;
      }
      return (
        <MenuManagement
          vendor={selectedVendor}
          onBack={handleBackToVendors}
          onAddMenuItem={handleAddMenuItem}
          onEditMenuItem={handleEditMenuItem}
          onDeleteMenuItem={handleDeleteMenuItem}
        />
      );
    }

    if (!canViewMenu(activeMenu)) {
      return <NoAccess />;
    }

    switch (activeMenu) {
      case 'dashboard':
        return (
          <Dashboard
            promotionStats={dashboardPromotionStats}
            onAddVendor={() => setShowVendorModal(true)}
          />
        );
      case 'vendors':
        return (
          <VendorList
            vendors={vendors}
            loading={vendorsLoading}
            onAddVendor={() => setShowVendorModal(true)}
            onManageVendor={handleVendorManage}
            onEditSections={handleEditSections}
            onEditDetails={handleEditDetails}
            onToggleStatus={handleToggleVendorStatus}
            onUpdateWait={handleUpdateVendorWait}
            onDeleteVendor={handleDeleteVendor}
          />
        );
      case 'users':
        return (
          <UsersManagement />
        );
      case 'events':
        return (
          <EventsPromotions 
            sharedData={sharedDataForEvents}
          />
        );
      case 'orders':
        return (
          <Orders
            promotions={promotions.filter((p: Promotion) => p.status === 'active')}
          />
        );
      case 'analytics':
        return (
          <Analytics
            data={enhancedAnalyticsData}
          />
        );
      case 'profile':
        return <Profile />;
      case 'venue-settings':
        return <VenueSettings />;
      default:
        return null;
    }
  };

  return (
    <div className={styles.layout}>
      {/* Sidebar Navigation */}
      <Sidebar
        activeMenu={activeMenu}
        selectedVendor={selectedVendor}
        onMenuClick={handleMenuClick}
        promotionStats={promotionStats}
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />

      {/* Main Content Area */}
      <div className={styles.content}>
        {/* Mobile top bar with hamburger */}
        <div className={styles.mobileTopBar}>
          <button
            className={styles.hamburger}
            onClick={() => setSidebarOpen(true)}
            aria-label="Open menu"
          >
            <svg width={22} height={22} viewBox="0 0 24 24" fill="currentColor">
              <path d="M3 18h18v-2H3v2zm0-5h18v-2H3v2zm0-7v2h18V6H3z"/>
            </svg>
          </button>
          <span className={styles.mobileTitle}>Admin Panel</span>
        </div>

        {renderMainContent()}
      </div>

      {/* Add Vendor Modal */}
      <Modal 
        isOpen={showVendorModal} 
        onClose={() => setShowVendorModal(false)}
        title="Add New Vendor"
      >
        <VendorForm
          onSubmit={handleCreateVendor}
          onCancel={() => setShowVendorModal(false)}
          selectedSections={selectedSections}
          onSectionToggle={setSelectedSections}
        />
      </Modal>

      {/* Edit Sections Modal */}
      <Modal 
        isOpen={showSectionModal} 
        onClose={closeSectionsModal}
        title={`Edit sections — ${vendorEditingSections?.name ?? ''}`}
        size="lg"
      >
        <StadiumMap
          selectedSections={selectedSections}
          onSectionToggle={setSelectedSections}
        />
        <div className={styles.modalActions}>
          <button
            onClick={closeSectionsModal}
            className="btn btn--secondary"
          >
            Cancel
          </button>
          <button
            onClick={handleUpdateSections}
            className="btn btn--primary"
          >
            Save sections
          </button>
        </div>
      </Modal>

      {/* Edit Vendor Details Modal */}
      <Modal
        isOpen={showVendorDetailsModal}
        onClose={() => {
          setShowVendorDetailsModal(false);
          setVendorBeingEdited(null);
          setVendorDetailsValid(false);
        }}
        title={vendorBeingEdited ? `Edit vendor — ${vendorBeingEdited.name}` : 'Edit vendor'}
        size="lg"
        footer={
          vendorBeingEdited ? (
            <>
              <button
                type="button"
                className={`${modalStyles.footerButton} ${modalStyles.footerButtonSecondary}`}
                onClick={() => {
                  setShowVendorDetailsModal(false);
                  setVendorBeingEdited(null);
                  setVendorDetailsValid(false);
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                form="vendor-details-form"
                disabled={!vendorDetailsValid}
                className={`${modalStyles.footerButton} ${modalStyles.footerButtonPrimary}`}
              >
                Save changes
              </button>
            </>
          ) : undefined
        }
      >
        {vendorBeingEdited && (
          <VendorDetailsForm
            vendor={vendorBeingEdited}
            hideFooter
            onValidityChange={setVendorDetailsValid}
            onSubmit={handleSaveVendorDetails}
            onCancel={() => {
              setShowVendorDetailsModal(false);
              setVendorBeingEdited(null);
              setVendorDetailsValid(false);
            }}
          />
        )}
      </Modal>

      {/* Menu Item Modal */}
      <Modal 
        isOpen={showMenuItemModal} 
        onClose={() => {
          setShowMenuItemModal(false);
          setEditingMenuItem(null);
          setMenuFormValid(false);
        }}
        title={editingMenuItem ? 'Edit Menu Item' : 'Add Menu Item'}
        size="xl"
        footer={
          <>
            <button
              type="button"
              className={`${modalStyles.footerButton} ${modalStyles.footerButtonSecondary}`}
              onClick={() => {
                setShowMenuItemModal(false);
                setEditingMenuItem(null);
                setMenuFormValid(false);
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              form="menu-item-form"
              disabled={!menuFormValid}
              className={`${modalStyles.footerButton} ${modalStyles.footerButtonPrimary}`}
            >
              {editingMenuItem ? 'Update item' : 'Add item'}
            </button>
          </>
        }
      >
        <MenuItemForm
          item={editingMenuItem}
          hideFooter
          onValidityChange={setMenuFormValid}
          onSubmit={handleSaveMenuItem}
          onCancel={() => {
            setShowMenuItemModal(false);
            setEditingMenuItem(null);
            setMenuFormValid(false);
          }}
        />
      </Modal>
    </div>
  );
};

export default AdminLayout;